import React from 'react'
import OperationWorkflowModal from 'common/components/OperationWorkflowModal'
import API from 'common/logic/API'
import { Button, Col, Form } from 'react-bootstrap'
import { Formik } from 'formik'
import * as yup from 'yup'
import { FinancialAsset, Fund } from 'types'

interface Props {
    onSuccess: (result: unknown) => void
    entryId?: number
}

function EditAssetWorkflow({ onSuccess, entryId }: Props) {
    const loadingStep = (_params: unknown) => {
        const toFetch: Record<string, any> = {
            funds: API.get('/funds')
        }
        if (entryId !== undefined) {
            toFetch.entry = API.get(`/financial-assets/${entryId}`)
        }
        return API.multiple(toFetch).then((r: any) => ({
            funds: r.funds.results as Fund[],
            entry: r.entry as FinancialAsset | undefined
        }))
    }

    const userInputStep = (_params: unknown, loaded: unknown, _vals: unknown, _errs: unknown, completion: (v: unknown) => void) => {
        const { funds, entry } = loaded as { funds: Fund[]; entry?: FinancialAsset }
        const allowedFunds = funds.filter((f) => f.isInvestment && !f.deleted)
        const fundsForForm = entry && !allowedFunds.some(f => f.id === entry.fund.id)
            ? [...allowedFunds, entry.fund]
            : allowedFunds
        return {
            title: entryId ? 'Edit asset' : 'New asset',
            component: <AssetForm funds={fundsForForm} entry={entry} completion={completion} />
        }
    }

    const executionStep = (_params: unknown, _loaded: unknown, userInput: unknown) => {
        if (entryId !== undefined) {
            return API.put(`/financial-assets/${entryId}`, userInput)
        }
        return API.post('/financial-assets', userInput)
    }

    return (
        <OperationWorkflowModal
            loadingStep={loadingStep}
            userInputStep={userInputStep}
            executionStep={executionStep}
            completionCallback={(r: unknown) => onSuccess(r && (r as any).data)}
        />
    )
}

interface FormProps {
    funds: Fund[]
    entry?: FinancialAsset
    completion: (v: unknown) => void
}

function AssetForm({ funds, entry, completion }: FormProps) {
    const schema = yup.object().shape({
        name: yup.string().required('Name is required'),
        fundId: yup.number().required('Fund is required'),
        notes: yup.string()
    })

    const initialValues = {
        name: entry?.name ?? '',
        fundId: entry?.fund.id ?? (funds[0]?.id ?? 0),
        notes: entry?.notes ?? ''
    }

    const handleSubmit = (values: typeof initialValues) => {
        completion({
            name: values.name,
            fundId: Number(values.fundId),
            notes: values.notes || null
        })
    }

    return (
        <Formik validationSchema={schema} onSubmit={handleSubmit} initialValues={initialValues}>
            {({ handleSubmit, handleChange, values, errors }) => (
                <Form noValidate onSubmit={handleSubmit}>
                    <Form.Row>
                        <Form.Group as={Col} controlId="assetName">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                placeholder="e.g. Bitcoin"
                                value={values.name}
                                onChange={handleChange}
                                isInvalid={!!errors.name}
                            />
                            <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group as={Col} controlId="assetFund">
                            <Form.Label>Fund</Form.Label>
                            <Form.Control
                                as="select"
                                name="fundId"
                                value={values.fundId}
                                onChange={handleChange}
                                isInvalid={!!errors.fundId}
                            >
                                {funds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                            </Form.Control>
                            <Form.Control.Feedback type="invalid">{errors.fundId}</Form.Control.Feedback>
                        </Form.Group>
                    </Form.Row>
                    <Form.Group controlId="assetNotes">
                        <Form.Label>Notes</Form.Label>
                        <Form.Control
                            type="text"
                            name="notes"
                            placeholder="Optional notes"
                            value={values.notes}
                            onChange={handleChange}
                        />
                    </Form.Group>
                    <Button variant="primary" type="submit">Save</Button>
                </Form>
            )}
        </Formik>
    )
}

export default EditAssetWorkflow
